import React, { useCallback, useEffect } from 'react';
import { RefreshCw, Wallet } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../hooks/useAppRedux';
import { useI18n } from '../../hooks/useI18n';
import { fetchWalletBalance } from '../../redux/slices/walletSlice';
import { classNames } from '../../utils/classNames';
import { useOpenSettings } from '../../features/settings/SettingsModalContext';

export const WalletBalanceBadge = ({ className = '', expanded = false }) => {
  const openSettings = useOpenSettings();
  const dispatch = useAppDispatch();
  const { t, formatNumber, locale } = useI18n();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const { balance, loading, refreshing } = useAppSelector((state) => state.wallet);

  const formatMoney = useCallback(
    (value) => {
      const formatted = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(Number(value) || 0);
      return `${formatNumber(formatted)} ${t('walletCurrency')}`;
    },
    [formatNumber, locale, t]
  );

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchWalletBalance());
    }
  }, [dispatch, isAuthenticated]);

  const goToWallet = useCallback(() => {
    openSettings('wallet');
  }, [openSettings]);

  const handleRefresh = useCallback(
    (event) => {
      event.stopPropagation();
      dispatch(fetchWalletBalance({ silent: true }));
    },
    [dispatch]
  );

  if (!isAuthenticated) {
    return null;
  }

  const isBusy = loading || refreshing;
  const displayBalance = balance === null ? '--' : formatMoney(balance);

  return (
    <div className={classNames('inline-flex h-11 items-center gap-1 rounded-lg border border-neutral-200 bg-white p-1 dark:border-neutral-800 dark:bg-neutral-950', className)}>
      <button
        type="button"
        onClick={goToWallet}
        className={classNames(
          'inline-flex min-w-0 items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium text-neutral-900 transition hover:bg-neutral-50 dark:text-neutral-100 dark:hover:bg-neutral-900 sm:gap-2 sm:px-2.5',
          expanded && 'flex-1'
        )}
        aria-label={t('walletBalanceLabel')}
      >
        <Wallet size={16} className="shrink-0 text-neutral-500" aria-hidden />
        <span
          className={classNames(
            'truncate tabular-nums',
            expanded ? 'flex-1 text-start' : 'max-w-[6.5rem] sm:max-w-none'
          )}
        >
          {isBusy && balance === null ? '...' : displayBalance}
        </span>
      </button>
      <button
        type="button"
        onClick={handleRefresh}
        disabled={isBusy}
        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-neutral-400 transition hover:bg-neutral-50 hover:text-neutral-800 disabled:opacity-50 dark:hover:bg-neutral-900 dark:hover:text-white"
        aria-label={t('walletRefresh')}
        title={t('walletRefresh')}
      >
        <RefreshCw size={14} className={classNames(refreshing && 'animate-spin')} aria-hidden />
      </button>
    </div>
  );
};
